import React, { useEffect, useState, useRef, useCallback, useMemo, memo } from 'react';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import './Animation.css';

const API = window.location.hostname === 'localhost'
  ? 'http://localhost:5000/api'
  : 'https://demo.myssa.net/api';

// ★ Libellés courts des en-têtes
const COLUMN_LABELS = {
  'Commande fournisseur'      : 'Cmde frn',
  'Nom fournisseur'           : 'Fournisseur',
  'Date de réception'         : 'Réception',
  'Quantité cmde frn allouée' : 'Qté frn allouée',
  'Numero de commande'        : 'Num Cmde',
  'Date de livraison prevue'  : 'Livraison prévue',
};

// ★ Largeurs des colonnes (px)
const COLUMN_WIDTHS = {
  'Date de livraison prevue'  : 100,
  Horizon                     : 80,
  Item                        : 120,
  Description                 : 200,
  'Numero de commande'        : 80,
  'N° ligne'                  : 55,
  Client                      : 120,
  'Commande fournisseur'      : 85,
  'Nom fournisseur'           : 120,
  'Date de réception'         : 90,
  'Quantité cmde frn allouée' : 85,
  'RAL'                       : 55,
  Stock                       : 60,
  Rupture                     : 70,
  Commentaires                : 260,
  Pilote                      : 100,
};
const DEFAULT_WIDTH = 100;

const INT_COLS  = ['RAL', 'Stock', 'Rupture'];
const HIDDEN    = ['_index', 'Service'];
const MONO_COLS = ['Item', 'Numero de commande', 'N° ligne', 'Commande fournisseur', 'Date de livraison prevue', 'Date de réception', 'Quantité cmde frn allouée', 'RAL', 'Stock'];
const NUM_COLS  = ['N° ligne', 'Quantité cmde frn allouée', 'RAL', 'Stock', 'Rupture'];

const HORIZON_JOURS = 60;  // ★ livraisons affichées jusqu'à aujourd'hui + 30 jours

// Les 3 états d'une ligne (même règle que l'original)
const ETATS = [
  { key: 'row-red',    tone: 'danger', label: 'Rupture critique', aide: 'Rupture sans commande frn, ou réception après la livraison' },
  { key: 'row-yellow', tone: 'warn',   label: 'Rupture suivie',   aide: 'Rupture couverte par une réception avant la livraison' },
  { key: 'row-green',  tone: 'ok',     label: 'OK',               aide: 'Pas de rupture' },
];
const TONE_ETAT = { 'row-red': 'danger', 'row-yellow': 'warn', 'row-green': 'ok' };

const formatDateFR = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d)) return value;
  return d.toLocaleDateString('fr-FR');
};

const cleanText = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const s = String(value).trim();
  if (['nan', 'undefined', 'null'].includes(s.toLowerCase())) return '';
  return s;
};

const parseDate = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && value.includes('/')) {
    const [day, month, year] = value.split('/');
    return new Date(`${year}-${month}-${day}`);
  }
  const d = new Date(value);
  return isNaN(d) ? null : d;
};

const getRowClass = (row) => {
  const rupture = Number(row.Rupture);
  if (rupture <= 0) return 'row-green';
  const dateReception = parseDate(row['Date de réception']);
  const dateLivraison = parseDate(row['Date de livraison prevue']);
  if (dateReception && dateLivraison) {
    if (dateReception > dateLivraison) return 'row-red';
    if (dateReception < dateLivraison) return 'row-yellow';
  }
  return 'row-red';
};

const ajusterHauteur = (el) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = el.scrollHeight + 'px';
};

const IconeLoupe = () => (
  <svg className="an-search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" />
  </svg>
);

const Message = ({ texte }) => {
  if (!texte) return null;
  const tone = texte.startsWith('✅') ? 'ok' : texte.startsWith('⚠️') ? 'warn' : 'danger';
  return <span className={`an-msg an-msg-${tone}`}>{texte.replace(/^(✅|⚠️|❌)\s*/, '')}</span>;
};

// ── Infobulle "délai d'appro" ──────────────────────────────────────────────
const STYLE_BULLE = {
  position: 'fixed',
  zIndex: 9999,
  pointerEvents: 'none',
  background: '#1f2937',
  color: '#fff',
  padding: '6px 10px',
  borderRadius: 6,
  fontSize: 12,
  lineHeight: 1.4,
  textAlign: 'left',
  whiteSpace: 'nowrap',
  boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
};

const formatDelai = (delai) => {
  if (delai === null || delai === undefined || delai === '') return 'Non renseigné';
  const n = Number(delai);
  if (isNaN(n)) return String(delai);
  return `${Math.round(n)} jour${Math.round(n) > 1 ? 's' : ''} ouvré${Math.round(n) > 1 ? 's' : ''}`;
};

// ★ Cache des délais déjà lus (item → { trouve, delai }) : un seul appel API par item
const cacheDelais = new Map();

const texteDelai = (info) => {
  if (!info)        return 'Chargement…';
  if (info.erreur)  return `Erreur : ${info.erreur}`;
  if (!info.trouve) return 'Item absent de la base article';
  return formatDelai(info.delai);
};

// Cellule Item : au survol, va chercher dans base_de_donne_item la ligne dont "Item" = cet item,
// puis affiche "Délai d'approvisionnement (jours ouvrés)" dans une bulle qui suit la souris
const ItemCell = ({ item }) => {
  const [bulle, setBulle] = useState(null);   // { x, y } ou null
  const [info, setInfo]   = useState(null);   // null = chargement
  const actifRef = useRef(false);

  const charger = async () => {
    const cle = cleanText(item);
    if (!cle) return;
    if (cacheDelais.has(cle)) { setInfo(cacheDelais.get(cle)); return; }
    setInfo(null);
    try {
      const res  = await fetch(`${API}/delai_appro_item?item=${encodeURIComponent(cle)}`);
      const json = await res.json();
      const val  = res.ok ? json : { erreur: json.error || `HTTP ${res.status}` };
      if (res.ok) cacheDelais.set(cle, val);
      if (actifRef.current) setInfo(val);
    } catch (err) {
      console.error('Erreur délai appro :', err);
      if (actifRef.current) setInfo({ erreur: 'API injoignable' });
    }
  };

  const entrer = (e) => { actifRef.current = true; setBulle({ x: e.clientX, y: e.clientY }); charger(); };
  const bouger = (e) => setBulle({ x: e.clientX, y: e.clientY });
  const sortir = () => { actifRef.current = false; setBulle(null); };

  return (
    <td
      className="an-mono"
      style={{ cursor: 'help' }}
      onMouseEnter={entrer}
      onMouseMove={bouger}
      onMouseLeave={sortir}
      onWheel={sortir}
    >
      {item}
      {bulle && item && createPortal(
        <div style={{ ...STYLE_BULLE, left: bulle.x + 12, top: bulle.y + 16 }} role="tooltip">
          <div style={{ opacity: 0.7, fontSize: 11 }}>Délai d'appro · {item}</div>
          <div style={{ fontWeight: 600 }}>{texteDelai(info)}</div>
        </div>,
        document.body
      )}
    </td>
  );
};

// ── Ligne ──────────────────────────────────────────────────────────────────
const AnimationRow = memo(({ row, columns, utilisateurs, editsRef, onEdit }) => {
  const rupture = Number(row.Rupture);
  const etat    = getRowClass(row);
  const tone    = TONE_ETAT[etat];

  const modifier = (champ, valeur) => {
    editsRef.current[row._index] = { ...editsRef.current[row._index], [champ]: valeur };
    onEdit(row._index);
  };

  return (
    <tr className={`an-row an-row-${tone}`}>
      {columns.map((col) => {
        if (col === 'Item') return (
          <ItemCell key={col} item={row[col]} />
        );

        if (col === 'Pilote') return (
          <td key={col}>
            {rupture === 0 ? '' : (
              <select
                className="an-select"
                defaultValue={editsRef.current[row._index]?.Pilote ?? (row.Pilote || '')}
                onChange={(e) => {
                  e.target.classList.toggle('an-select-set', Boolean(e.target.value));
                  modifier('Pilote', e.target.value);
                }}
                aria-label="Pilote"
                ref={(el) => { if (el) el.classList.toggle('an-select-set', Boolean(el.value)); }}
              >
                <option value="">— Choisir —</option>
                {utilisateurs.map((u, i) => (
                  <option key={i} value={u.Prenom}>{u.Prenom}</option>
                ))}
              </select>
            )}
          </td>
        );

        if (col === 'Commentaires') return (
          <td key={col} className="an-td-comment">
            {rupture === 0 ? '' : (
              <textarea
                className="an-textarea"
                rows={1}
                placeholder="Action, relance, date promise…"
                defaultValue={editsRef.current[row._index]?.Commentaires ?? (row.Commentaires || '')}
                onChange={(e) => { ajusterHauteur(e.target); modifier('Commentaires', e.target.value); }}
                ref={ajusterHauteur}
                aria-label="Commentaire"
              />
            )}
          </td>
        );

        if (col === 'Date de livraison prevue') return (
          <td key={col} className="an-mono an-strong">{formatDateFR(row[col])}</td>
        );

        if (col === 'Date de réception') {
          const dates = (row[col] || '').split('\n').map(v => formatDateFR(v.trim())).filter(Boolean);
          return (
            <td key={col} className="an-mono an-pre">
              {dates.length ? dates.join('\n') : <span className="an-muted">—</span>}
            </td>
          );
        }

        if (col === 'Quantité cmde frn allouée') {
          const vals = (row[col] || '').split('\n').map((v) => { const n = Number(v); return isNaN(n) ? v : Math.round(n); });
          return <td key={col} className="an-mono an-pre num">{vals.join('\n')}</td>;
        }

        if (col === 'Rupture') return (
          <td key={col} className="num">
            {row[col] != null && row[col] !== ''
              ? <span className={`an-badge an-badge-${rupture > 0 ? tone : 'ok'}`}>{Math.round(rupture)}</span>
              : ''}
          </td>
        );

        if (col === 'Horizon') return (
          <td key={col}>{row[col] ? <span className="an-badge an-badge-neutral">{row[col]}</span> : ''}</td>
        );

        const classes = [
          MONO_COLS.includes(col) ? 'an-mono' : '',
          NUM_COLS.includes(col) ? 'num' : '',
          col === 'Client' ? 'an-client' : '',
          col === 'Description' ? 'an-desc' : '',
        ].filter(Boolean).join(' ');

        return (
          <td key={col} className={classes}>
            {INT_COLS.includes(col) && row[col] != null && row[col] !== '' ? Math.round(Number(row[col])) : row[col]}
          </td>
        );
      })}
    </tr>
  );
});

// ── Composant principal ───────────────────────────────────────────────────
// posX / posY / width / height : gardés pour compatibilité. S'ils sont fournis,
// le bloc est positionné en absolu comme avant ; sinon il suit la mise en page normale.
const Animation = ({ posX, posY, width, height }) => {
  const [data, setData]                           = useState([]);
  const [utilisateurs, setUtilisateurs]           = useState([]);
  const [loading, setLoading]                     = useState(true);
  const [showOnlyManquants, setShowOnlyManquants] = useState(false);
  const [etatFiltre, setEtatFiltre]               = useState('');   // '', 'row-red', 'row-yellow', 'row-green'
  const [itemFilter, setItemFilter]               = useState('');
  const [commandeFilter, setCommandeFilter]       = useState('');
  const [clientFilter, setClientFilter]           = useState('');
  const [isSaving, setIsSaving]                   = useState(false);
  const [saveMsg, setSaveMsg]                     = useState('');
  const [nbModifs, setNbModifs]                   = useState(0);    // lignes modifiées non enregistrées

  const editsRef = useRef({});
  const dataRef  = useRef([]);
  useEffect(() => { dataRef.current = data; }, [data]);

  useEffect(() => {
    document.title = 'MYSSA – Animation';
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const jsonUtilisateurs = [
          { Prenom: 'ADV', Service: 'ADV' }, { Prenom: 'APPRO', Service: 'APPRO' },
          { Prenom: 'LOG', Service: 'LOG' },
        ];
        setUtilisateurs(jsonUtilisateurs);

        const resAnim = await fetch(`${API}/animation`);
        const rawAnim = await resAnim.json();
        const resCom  = await fetch(`${API}/base_commentaire_animation`);
        const rawCom  = await resCom.json();

        const merged = rawAnim.map(({ ID, Client_id, ...row }, index) => {
          const match = rawCom.find(
            (c) => String(c['Numero de commande']).trim() === String(row['Numero de commande']).trim() &&
                   String(c['N° ligne']).trim()           === String(row['N° ligne']).trim()
          );
          return { ...row, _index: index, Commentaires: cleanText(match?.Commentaires ?? row.Commentaires), Pilote: cleanText(match?.Pilote ?? row.Pilote) };
        });

        merged.sort((a, b) => {
          const dA = a['Date de livraison prevue'] ? new Date(a['Date de livraison prevue']) : new Date(0);
          const dB = b['Date de livraison prevue'] ? new Date(b['Date de livraison prevue']) : new Date(0);
          return dA - dB;
        });

        setData(merged);
        editsRef.current = {};
        setNbModifs(0);
      } catch (err) {
        console.error('Erreur chargement Animation:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Compte les lignes modifiées (fonction stable → les lignes mémorisées ne se redessinent pas)
  const handleEdit = useCallback(() => {
    setNbModifs(Object.keys(editsRef.current).length);
  }, []);

  const getRowWithEdits = (row) => ({ ...row, ...(editsRef.current[row._index] || {}) });

  const sauvegarderLigne = async (row) => {
    const r        = getRowWithEdits(row);
    if (Number(r.Rupture) === 0) return;
    const numCmde  = String(r['Numero de commande']).trim();
    const numLigne = String(r['N° ligne']).trim();
    const pilote   = cleanText(r.Pilote);
    const comment  = cleanText(r.Commentaires);

    let numeroTache = null;
    try {
      const res = await fetch(`${API}/base_commentaire_animation`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ numero_commande: numCmde, numero_ligne: numLigne, commentaire: comment, pilote, client: cleanText(r.Client), item: cleanText(r.Item), commande_fournisseur: cleanText(r['Commande fournisseur']), nom_fournisseur: cleanText(r['Nom fournisseur']), date_reception: r['Date de réception'] || null, quantite_cmde_frn_allouee: cleanText(r['Quantité cmde frn allouée']), rupture: Number(r.Rupture) }),
      });
      const json = await res.json();
      numeroTache = json.numero_tache;
    } catch (err) { console.error('❌ base_commentaire_animation :', err); return; }

    if (!pilote || !comment || !numeroTache) return;

    const dateLivraison = r['Date de livraison prevue']
      ? new Date(r['Date de livraison prevue']).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      : '';
    const description = `Date de livraison prevue : ${dateLivraison} Item : ${cleanText(r.Item)} Commande client : ${numCmde} Ligne : ${numLigne}\nCommande fournisseur : ${cleanText(r['Commande fournisseur'])} Nom fournisseur : ${cleanText(r['Nom fournisseur'])} Date de réception : ${formatDateFR(r['Date de réception'])}`;

    try {
      await fetch(`${API}/taches`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ numero_tache: numeroTache, description, commentaires: comment, departement: pilote, de: 'Animation', source: 'animation' }),
      });
    } catch (err) { console.error('❌ taches :', err); }
  };

  const handleSaveAll = useCallback(async () => {
    setIsSaving(true); setSaveMsg('');
    const lignes = dataRef.current.filter((r) => Number(r.Rupture) !== 0);
    let ok = 0, ko = 0;
    for (const row of lignes) {
      const r = getRowWithEdits(row);
      if (!cleanText(r.Pilote) && !cleanText(r.Commentaires)) continue;
      try { await sauvegarderLigne(row); ok++; } catch { ko++; }
    }
    setIsSaving(false);
    if (ko === 0) setNbModifs(0);
    setSaveMsg(ko === 0 ? `✅ ${ok} ligne(s) enregistrée(s)` : `⚠️ ${ok} OK / ${ko} erreur(s)`);
    setTimeout(() => setSaveMsg(''), 4000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fenêtre de dates + filtres texte (avant le filtre par état → sert aux compteurs)
  const baseData = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const limite = new Date(today);
    limite.setDate(limite.getDate() + HORIZON_JOURS);

    return data
      .filter((r) => {
        const d = parseDate(r['Date de livraison prevue']);
        return d && d <= limite;
      })
      .filter((r) => !itemFilter     || r.Item?.toLowerCase().includes(itemFilter.toLowerCase()))
      .filter((r) => !commandeFilter || String(r['Numero de commande']).toLowerCase().includes(commandeFilter.toLowerCase()))
      .filter((r) => !clientFilter   || r.Client?.toLowerCase().includes(clientFilter.toLowerCase()));
  }, [data, itemFilter, commandeFilter, clientFilter]);

  const compteurs = useMemo(() => {
    const c = { 'row-red': 0, 'row-yellow': 0, 'row-green': 0 };
    baseData.forEach(r => { c[getRowClass(r)]++; });
    return c;
  }, [baseData]);

  const filteredData = useMemo(() => (
    baseData
      .filter((r) => !showOnlyManquants || Number(r.Rupture) !== 0)
      .filter((r) => !etatFiltre || getRowClass(r) === etatFiltre)
  ), [baseData, showOnlyManquants, etatFiltre]);

  const columns = useMemo(() =>
    data.length > 0 && data[0] != null ? Object.keys(data[0]).filter((col) => !HIDDEN.includes(col)) : [],
  [data]);

  const tableWidth = columns.reduce((s, c) => s + (COLUMN_WIDTHS[c] || DEFAULT_WIDTH), 0);

  const exportToExcel = () => {
    const cleanedData = filteredData.map(({ _index, ...row }) => {
      const r = getRowWithEdits({ _index, ...row });
      INT_COLS.forEach((col) => { if (r[col] != null) r[col] = Math.round(Number(r[col])); });
      const { _index: _i, ...rest } = r;
      return rest;
    });
    const ws = XLSX.utils.json_to_sheet(cleanedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Analyse');
    const parts = [];
    if (itemFilter)     parts.push(`Item-${itemFilter}`);
    if (clientFilter)   parts.push(`Client-${clientFilter}`);
    if (commandeFilter) parts.push(`Commande-${commandeFilter}`);
    saveAs(
      new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })], { type: 'application/octet-stream' }),
      `analyse_rupture${parts.length ? '_' + parts.join('_') : ''}_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  const filtresTexte = [
    { label: 'Item',        placeholder: 'Item…',        value: itemFilter,     setter: setItemFilter },
    { label: 'Client',      placeholder: 'Client…',      value: clientFilter,   setter: setClientFilter },
    { label: 'N° commande', placeholder: 'N° commande…', value: commandeFilter, setter: setCommandeFilter },
  ];

  const positionAncienne = posX !== undefined || posY !== undefined
    ? { position: 'absolute', left: posX ?? 0, top: posY ?? 0, width: width ?? '95%', height: height ?? 'auto' }
    : undefined;

  const totalRuptures = compteurs['row-red'] + compteurs['row-yellow'];

  return (
    <div className="an-page" style={positionAncienne}>
      {/* ★ En-têtes et données centrés dans le tableau (prioritaire sur Animation.css,
            y compris la classe "num" qui aligne les quantités à droite).
            Exception : le texte saisi dans la zone Commentaires reste aligné à gauche. */}
      <style>{`
        .an-table th, .an-table th.num,
        .an-table td, .an-table td.num { text-align: center !important; vertical-align: middle; }
        .an-table .an-textarea { text-align: left; }
      `}</style>
      <div className="an-container">

        {/* ── En-tête ── */}
        <div className="an-header">
          <div className="an-title-row">
            <span className="an-accent" />
            <h1 className="an-title">Animation des ruptures</h1>
            <span className="an-chip-dept">DRC · APPRO · LOG</span>
          </div>
          <div className="an-subtitle">
         
          </div>
        </div>

        {/* ── Compteurs = filtres par état ── */}
        <div className="an-kpis" role="group" aria-label="Filtrer par état">
          {ETATS.map(({ key, tone, label, aide }) => {
            const actif = etatFiltre === key;
            return (
              <button
                key={key}
                type="button"
                className={`an-kpi an-kpi-${tone} ${actif ? 'active' : ''}`}
                aria-pressed={actif}
                title={aide}
                onClick={() => setEtatFiltre(actif ? '' : key)}
              >
                <span className="an-kpi-dot" />
                <span className="an-kpi-label">{label}</span>
                <span className="an-kpi-value">{compteurs[key]}</span>
              </button>
            );
          })}
          <div className="an-kpi-total">
            <span className="an-kpi-total-value">{totalRuptures}</span> rupture{totalRuptures > 1 ? 's' : ''} sur {baseData.length} ligne{baseData.length > 1 ? 's' : ''}
          </div>
        </div>

        {/* ── Barre d'outils ── */}
        <div className="an-toolbar">
          {filtresTexte.map(({ label, placeholder, value, setter }) => (
            <div key={label} className="an-search">
              <IconeLoupe />
              <input
                type="text"
                className={`an-input ${value ? 'active' : ''}`}
                placeholder={placeholder}
                aria-label={`Filtrer par ${label}`}
                value={value}
                onChange={(e) => setter(e.target.value)}
              />
              {value && <button type="button" className="an-clear" aria-label={`Effacer ${label}`} onClick={() => setter('')}>×</button>}
            </div>
          ))}

          <label className={`an-toggle ${showOnlyManquants ? 'active' : ''}`}>
            <input type="checkbox" checked={showOnlyManquants} onChange={(e) => setShowOnlyManquants(e.target.checked)} />
            Ruptures uniquement
          </label>

          <span className="an-sep" />

          <button type="button" className="an-btn an-btn-ghost" onClick={exportToExcel} disabled={filteredData.length === 0}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M5 21h14" /></svg>
            Export Excel
          </button>

          <div className="an-toolbar-right">
            <Message texte={saveMsg} />
            <button type="button" className={`an-btn an-btn-primary ${nbModifs ? 'an-btn-pending' : ''}`} onClick={handleSaveAll} disabled={isSaving}>
              {isSaving ? (
                <><span className="an-spinner" aria-hidden="true" /> Enregistrement…</>
              ) : (
                <>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 3h11l3 3v15H5z" /><path d="M8 3v5h8V3" /><path d="M8 14h8v7H8z" /></svg>
                  Enregistrer tout{nbModifs ? ` (${nbModifs})` : ''}
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Tableau ── */}
        <div className="an-card an-table-card">
          <div className="an-table-head">
            <div className="an-table-head-left">
              <span className="an-table-title">Lignes à animer</span>
              <span className="an-count">{filteredData.length}</span>
              {etatFiltre && (
                <button type="button" className="an-filter-chip" onClick={() => setEtatFiltre('')}>
                  {ETATS.find(e => e.key === etatFiltre)?.label} <span aria-hidden="true">×</span>
                </button>
              )}
            </div>
            {nbModifs > 0 && !isSaving && (
              <span className="an-pending-note">{nbModifs} ligne{nbModifs > 1 ? 's' : ''} modifiée{nbModifs > 1 ? 's' : ''} — pense à enregistrer</span>
            )}
          </div>

          <div className="an-table-scroll">
            {loading ? (
              <div className="an-empty">Chargement des données…</div>
            ) : filteredData.length === 0 ? (
              <div className="an-empty">Aucune ligne ne correspond aux filtres.</div>
            ) : (
              <table className="an-table" style={{ width: `${tableWidth}px` }}>
                <colgroup>
                  {columns.map(c => <col key={c} style={{ width: `${COLUMN_WIDTHS[c] || DEFAULT_WIDTH}px` }} />)}
                </colgroup>
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col} className={NUM_COLS.includes(col) ? 'num' : ''} title={col}>
                        {COLUMN_LABELS[col] || col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((row) => (
                    <AnimationRow
                      key={row._index}
                      row={row}
                      columns={columns}
                      utilisateurs={utilisateurs}
                      editsRef={editsRef}
                      onEdit={handleEdit}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Animation;